import { useState, useEffect, useRef } from "react";
import { MessageCircle, ClipboardList, X, Plus } from "lucide-react";

export default function FloatingActionButton({ whatsapp }) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const phone = (whatsapp || "657871824").replace(/\D/g, "");
  const waMsg = encodeURIComponent("Hola, me gustaría obtener más información sobre el Campus Verano Activo La Estanca 2026.");
  const waUrl = `https://wa.me/34${phone}?text=${waMsg}`;

  // Mostrar FAB solo después de hacer scroll (evita solapamiento con CTAs del Hero)
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 320);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close when clicking outside the FAB
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="fixed z-40 transition-all duration-300"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        right: "20px",
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1)" : "scale(0.7)",
        pointerEvents: visible ? "auto" : "none",
      }}
      data-testid="floating-action-button"
    >
      {/* Sub-buttons — slide up when open */}
      <div
        className="flex flex-col items-end gap-3 mb-3 transition-all duration-200"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0) scale(1)" : "translateY(16px) scale(0.95)",
          pointerEvents: open ? "auto" : "none",
        }}
        aria-hidden={!open}
      >
        {/* WhatsApp */}
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="fab-whatsapp-btn"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-full shadow-lg transition-transform active:scale-95 hover:scale-105"
          style={{ background: "#25D366" }}
        >
          <span className="text-white font-['Montserrat'] font-semibold text-sm whitespace-nowrap leading-none">
            WhatsApp
          </span>
          <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={18} color="white" />
          </span>
        </a>

        {/* Inscripción */}
        <a
          href="#inscripcion"
          data-testid="fab-register-btn"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-full shadow-lg transition-transform active:scale-95 hover:scale-105"
          style={{ background: "#0072A4" }}
        >
          <span className="text-white font-['Montserrat'] font-semibold text-sm whitespace-nowrap leading-none">
            Inscripción
          </span>
          <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <ClipboardList size={18} color="white" />
          </span>
        </a>
      </div>

      {/* Main toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="fab-main-btn"
        aria-label={open ? "Cerrar menú" : "Acciones rápidas"}
        aria-expanded={open}
        className="w-13 h-13 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        style={{
          width: 52,
          height: 52,
          background: open
            ? "#64748B"
            : "linear-gradient(135deg, #F58220 0%, #c9600d 100%)",
        }}
      >
        <span
          className="transition-transform duration-200"
          style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
        >
          {open ? <X size={22} color="white" /> : <Plus size={22} color="white" />}
        </span>
      </button>
    </div>
  );
}
